/**
 * Single source of truth for sidebar nav. Order, grouping and labels match
 * the prototype `grhamoy_erp.html`. `moduleId` matches `module.id` in DB
 * (used for permission checks via useAuth().hasPerm).
 */

export interface SidebarItem {
  moduleId: string;
  label: string;
  path: string;
}

export interface SidebarGroup {
  id: string;
  label: string;
  items: SidebarItem[];
}

// The home dashboard item — now the first entry in the "Dashboard" group below.
export const DASHBOARD: SidebarItem = {
  moduleId: 'dashboard',
  label: 'Dashboard',
  path: '/',
};

export const SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    id: 'dashboards',
    label: 'Dashboard',
    items: [
      DASHBOARD,
    ],
  },
  {
    id: 'purchase',
    label: 'Procurement',
    items: [
      { moduleId: 'mrn',            label: 'MRN',                path: '/purchase/mrn' },
      { moduleId: 'hod',            label: 'HOD Approval',       path: '/purchase/hod' },
      { moduleId: 'invcheck',       label: 'Inventory Check',    path: '/purchase/invcheck' },
      { moduleId: 'pr',             label: 'Purchase Req.',      path: '/purchase/pr' },
      { moduleId: 'pr-approval',    label: 'PR Approval',        path: '/purchase/pr-approval' },
      { moduleId: 'rfq',            label: 'Quotations / RFQ',   path: '/purchase/rfq' },
      { moduleId: 'ai',             label: 'AI Analysis',        path: '/purchase/ai' },
      { moduleId: 'quoteapproval',  label: 'Quote Approval',     path: '/purchase/quoteapproval' },
      { moduleId: 'po',             label: 'Purchase Orders',    path: '/purchase/po' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales Management',
    items: [
      { moduleId: 'catalogue',      label: 'Product Catalogue',  path: '/sales/catalogue' },
      { moduleId: 'lead-dashboard',    label: 'Lead Dashboard',    path: '/sales/lead-dashboard' },
      { moduleId: 'enquiry-dashboard', label: 'Enquiry Dashboard', path: '/sales/enquiry-dashboard' },
      { moduleId: 'enq-review',     label: 'Order Review',       path: '/sales/enq-review' },
      { moduleId: 'dispatch-order', label: 'Dispatch Order',     path: '/sales/dispatch-order' },
      { moduleId: 'credit-check',   label: 'Credit Check',       path: '/sales/credit-check' },
      { moduleId: 'gate-pass',      label: 'Gate Pass',          path: '/sales/gate-pass' },
      { moduleId: 'fulfillment',    label: 'Fulfillment',        path: '/sales/fulfillment' },
    ],
  },
  {
    // Warehouse floor work — goods in (GRN, stock entry), goods out (pick, pack,
    // dispatch), returns and internal moves. Paths are unchanged, so links keep working.
    id: 'store-ops',
    label: 'Store Operations',
    items: [
      { moduleId: 'grn',            label: 'GRN',                path: '/purchase/grn' },
      { moduleId: 'stock',          label: 'Stock Entry',        path: '/purchase/stock' },
      { moduleId: 'pick-list',      label: 'Pick List',          path: '/sales/pick-list' },
      { moduleId: 'packing',        label: 'Packing',            path: '/sales/packing' },
      { moduleId: 'outward-grn',    label: 'Dispatch Note',      path: '/sales/outward-grn' },
      { moduleId: 'sales-return',   label: 'Sales Return',       path: '/sales/sales-return' },
      { moduleId: 'dept-transfer',  label: 'Department Transfer', path: '/inventory/dept-transfer' },
    ],
  },
  {
    id: 'shopify',
    label: 'Ecommerce',
    items: [
      { moduleId: 'ecom-dashboard',  label: 'Ecommerce Dashboard', path: '/sales/ecom-dashboard' },
      { moduleId: 'online-orders',   label: 'Online Orders', path: '/sales/online-orders' },
      { moduleId: 'shipping',        label: 'Shipping',      path: '/sales/shipping' },
      { moduleId: 'online-profitability', label: 'Online Profitability', path: '/sales/online-profitability' },
      { moduleId: 'shopify-stock',   label: 'Ecom Inventory',    path: '/admin/configurations/shopify-stock' },
      { moduleId: 'shopify-mapping', label: 'SKU Mapping',   path: '/admin/configurations/shopify-mapping' },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    items: [
      { moduleId: 'wh-stock',           label: 'Warehouse Stock', path: '/inventory/wh-stock' },
      { moduleId: 'wh-audit',           label: 'Stock Audit',     path: '/inventory/wh-audit' },
      { moduleId: 'wh-ledger',          label: 'WH Ledger',       path: '/inventory/wh-ledger' },
      { moduleId: 'wh-serials',         label: 'Tracker',         path: '/inventory/wh-serials' },
      { moduleId: 'wh-ageing',          label: 'Stock Ageing',    path: '/inventory/wh-ageing' },
      { moduleId: 'sales-orders-field', label: 'Field Orders',    path: '/inventory/sales-orders-field' },
    ],
  },
  {
    id: 'sis',
    label: 'SIS Management',
    items: [
      { moduleId: 'cs-dashboard',  label: 'SIS Overview', path: '/inventory/sis-dashboard' },
      { moduleId: 'sis-analytics',  label: 'SIS Analytics', path: '/inventory/sis-analytics' },
      { moduleId: 'sis-stock',     label: 'SIS Stock',    path: '/inventory/sis-stock' },
      { moduleId: 'sis-transfer',   label: 'Stock Transfer', path: '/inventory/sis-transfer' },
      { moduleId: 'sis-sales',      label: 'Sales Report',   path: '/inventory/sis-sales' },
      { moduleId: 'sis-ledger',     label: 'SIS Ledger',     path: '/inventory/sis-ledger' },
      { moduleId: 'sis-exceptions', label: 'SIS Exceptions', path: '/inventory/sis-exceptions' },
      { moduleId: 'sis-aging',      label: 'Sell-through & Aging', path: '/inventory/sis-aging' },
      { moduleId: 'sis-settlement', label: 'SIS Settlement', path: '/inventory/sis-settlement' },
      { moduleId: 'sis-cn-dn',      label: 'SIS Credit/Debit Notes', path: '/inventory/sis-cn-dn' },
      { moduleId: 'sis-profitability', label: 'SIS Profitability', path: '/inventory/sis-profitability' },
      { moduleId: 'sis-return-pool', label: 'SIS Returns', path: '/inventory/sis-returns' },
      { moduleId: 'sis-assets',     label: 'SIS Assets',     path: '/inventory/sis-assets' },
    ],
  },
  {
    id: 'accounts',
    label: 'Accounts',
    items: [
      { moduleId: 'sp-invoice',     label: 'Invoice',            path: '/sales/sp-invoice' },
      { moduleId: 'acc-invoice-logs', label: 'Invoice Logs',     path: '/accounts/invoice-logs' },
      { moduleId: 'acc-customer-os', label: 'Customer Outstanding', path: '/accounts/customer-os' },
      { moduleId: 'acc-supplier-os', label: 'Supplier Outstanding', path: '/accounts/supplier-os' },
      { moduleId: 'acc-sales-register', label: 'Sales Register', path: '/accounts/sales-register' },
      { moduleId: 'acc-purchase-register', label: 'Purchase Register', path: '/accounts/purchase-register' },
      { moduleId: 'acc-receipt',       label: 'Receipt',          path: '/accounts/receipt' },
      { moduleId: 'acc-payment',       label: 'Payment',          path: '/accounts/payment' },
      { moduleId: 'acc-contra',        label: 'Contra',           path: '/accounts/contra' },
      { moduleId: 'acc-journal',       label: 'Journal',          path: '/accounts/journal' },
      { moduleId: 'acc-sales',         label: 'Sales Voucher',    path: '/accounts/sales' },
      { moduleId: 'acc-purchase',      label: 'Purchase Voucher', path: '/accounts/purchase' },
      { moduleId: 'acc-notes',         label: 'Credit & Debit Notes', path: '/accounts/notes' },
      { moduleId: 'acc-edit-log',      label: 'Voucher Edit Log', path: '/accounts/edit-log' },
      { moduleId: 'acc-groups',        label: 'Account Groups',   path: '/accounts/groups' },
      { moduleId: 'acc-ledgers',       label: 'Ledgers',          path: '/accounts/ledgers' },
      { moduleId: 'acc-voucher-types', label: 'Voucher Types',    path: '/accounts/voucher-types' },
      { moduleId: 'acc-tds',           label: 'TDS Nature of Payment', path: '/accounts/tds' },
      { moduleId: 'acc-cost-centres',  label: 'Cost Centres',     path: '/accounts/cost-centres' },
    ],
  },
  {
    id: 'acc-reports',
    label: 'Account Reports',
    items: [
      { moduleId: 'acc-day-book',       label: 'Day Book',               path: '/accounts/day-book' },
      { moduleId: 'acc-cash-book',      label: 'Cash Book',              path: '/accounts/cash-book' },
      { moduleId: 'acc-cash-bank-book', label: 'Cash/Bank Book',         path: '/accounts/cash-bank-book' },
      { moduleId: 'acc-trial-balance',  label: 'Trial Balance',          path: '/accounts/trial-balance' },
      { moduleId: 'acc-profit-loss',    label: 'Profit & Loss A/c',      path: '/accounts/profit-loss' },
      { moduleId: 'acc-balance-sheet',  label: 'Balance Sheet',          path: '/accounts/balance-sheet' },
      { moduleId: 'acc-receivable',     label: 'Receivable Outstanding', path: '/accounts/receivable' },
      { moduleId: 'acc-payable',        label: 'Payable Outstanding',    path: '/accounts/payable' },
      { moduleId: 'acc-expense-report', label: 'Expense Report',         path: '/accounts/expense-report' },
      { moduleId: 'acc-income-report',  label: 'Income Report',          path: '/accounts/income-report' },
      { moduleId: 'acc-group-summary',  label: 'Group-wise Ledger Report', path: '/accounts/group-summary' },
      { moduleId: 'acc-closing-stock',  label: 'Closing Stock',          path: '/accounts/closing-stock' },
    ],
  },
  {
    id: 'acc-exp-reports',
    label: 'Expenses Reports',
    items: [
      { moduleId: 'acc-exp-entry', label: 'Expense Entry', path: '/accounts/expenses/entry' },
      { moduleId: 'acc-exp-head', label: 'Expense Head-wise Report', path: '/accounts/expenses/heads' },
      { moduleId: 'acc-exp-month', label: 'Monthly Expense Report', path: '/accounts/expenses/monthly' },
      { moduleId: 'acc-exp-dept', label: 'Department-wise Expense', path: '/accounts/expenses/departments' },
      { moduleId: 'acc-exp-budget', label: 'Expense vs Budget Report', path: '/accounts/expenses/budget' },
      { moduleId: 'acc-exp-tds-prof', label: 'TDS on Professional Charges', path: '/accounts/expenses/tds-professional' },
      { moduleId: 'acc-exp-tds-con', label: 'TDS on Contractor Payment', path: '/accounts/expenses/tds-contractor' },
      { moduleId: 'acc-exp-tds-other', label: 'Other Applicable TDS Entries', path: '/accounts/expenses/tds-other' },
    ],
  },
  {
    id: 'acc-gst-reports',
    label: 'GST Reports',
    items: [
      { moduleId: 'acc-gst-sales', label: 'GST Sales Register', path: '/accounts/gst/sales' },
      { moduleId: 'acc-gst-purchase', label: 'GST Purchase Register', path: '/accounts/gst/purchase' },
      { moduleId: 'acc-gst-hsn', label: 'HSN-wise Report', path: '/accounts/gst/hsn' },
      { moduleId: 'acc-gst-rate', label: 'GST Rate-wise Report', path: '/accounts/gst/rates' },
      { moduleId: 'acc-gst-heads', label: 'CGST / SGST / IGST Report', path: '/accounts/gst/heads' },
      { moduleId: 'acc-gst-itc', label: 'Input Tax Credit (ITC)', path: '/accounts/gst/itc' },
      { moduleId: 'acc-gst-output', label: 'Output GST Report', path: '/accounts/gst/output' },
      { moduleId: 'acc-gst-r1', label: 'GSTR-1 Data', path: '/accounts/gst/gstr1' },
      { moduleId: 'acc-gst-3b', label: 'GSTR-3B Data', path: '/accounts/gst/gstr3b' },
      { moduleId: 'acc-gst-2b', label: 'GSTR-2B Reconciliation', path: '/accounts/gst/gstr2b' },
      { moduleId: 'acc-gst-portal', label: 'Books vs GST Portal', path: '/accounts/gst/portal' },
      { moduleId: 'acc-gst-mismatch', label: 'GST Invoice Mismatch', path: '/accounts/gst/mismatch' },
      { moduleId: 'acc-gst-einv', label: 'E-Invoice Details', path: '/accounts/gst/einvoice' },
      { moduleId: 'acc-gst-ewb', label: 'E-Way Bill Details', path: '/accounts/gst/ewaybill' },
    ],
  },
  {
    id: 'acc-tds-reports',
    label: 'TDS Reports',
    items: [
      { moduleId: 'acc-tds-deductions', label: 'TDS Deduction Entry', path: '/accounts/tds/deductions' },
      { moduleId: 'acc-tds-sections', label: 'TDS Section-wise Report', path: '/accounts/tds/sections' },
      { moduleId: 'acc-tds-rates', label: 'TDS Rate Auto Calculation', path: '/accounts/tds/rates' },
      { moduleId: 'acc-tds-party', label: 'Party-wise TDS Ledger', path: '/accounts/tds/party' },
      { moduleId: 'acc-tds-pan', label: 'PAN-wise TDS Report', path: '/accounts/tds/pan' },
      { moduleId: 'acc-tds-deducted', label: 'TDS Deducted Report', path: '/accounts/tds/deducted' },
      { moduleId: 'acc-tds-payable', label: 'TDS Payable Report', path: '/accounts/tds/payable' },
      { moduleId: 'acc-tds-challans', label: 'TDS Challan Entry', path: '/accounts/tds/challans' },
      { moduleId: 'acc-tds-adjust', label: 'Challan-wise Adjustment', path: '/accounts/tds/adjust' },
      { moduleId: 'acc-tds-return', label: 'TDS Return Data', path: '/accounts/tds/return' },
      { moduleId: 'acc-tds-16a', label: 'Form 16A Details', path: '/accounts/tds/form-16a' },
      { moduleId: 'acc-tds-rec', label: 'TDS Reconciliation', path: '/accounts/tds/reconciliation' },
      { moduleId: 'acc-tds-outstanding', label: 'TDS Outstanding Report', path: '/accounts/tds/outstanding' },
      { moduleId: 'acc-tds-due', label: 'TDS Due Date Reminder', path: '/accounts/tds/due-dates' },
      { moduleId: 'acc-tds-ldc', label: 'Lower / Nil TDS Certificates', path: '/accounts/tds/ldc' },
      { moduleId: 'acc-tds-threshold', label: 'TDS Threshold Limit Tracking', path: '/accounts/tds/thresholds' },
    ],
  },
  {
    id: 'acc-rec-reports',
    label: 'Reconciliation',
    items: [
      { moduleId: 'acc-rec-brs', label: 'Bank Reconciliation (BRS)', path: '/accounts/reconciliation/bank' },
      { moduleId: 'acc-rec-gst', label: 'GST Reconciliation', path: '/accounts/reconciliation/gst' },
      { moduleId: 'acc-rec-2b', label: 'GSTR-2B Reconciliation', path: '/accounts/reconciliation/gstr2b' },
      { moduleId: 'acc-rec-sales', label: 'Sales Reconciliation', path: '/accounts/reconciliation/sales' },
      { moduleId: 'acc-rec-purchase', label: 'Purchase Reconciliation', path: '/accounts/reconciliation/purchase' },
      { moduleId: 'acc-rec-tds', label: 'TDS Reconciliation', path: '/accounts/reconciliation/tds' },
      { moduleId: 'acc-rec-party', label: 'Party Statement Reconciliation', path: '/accounts/reconciliation/party' },
      { moduleId: 'acc-rec-invoice', label: 'Invoice-wise Reconciliation', path: '/accounts/reconciliation/invoices' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    items: [
      { moduleId: 'rpt-meca',       label: 'MeCA Report',        path: '/reports/meca' },
    ],
  },
  {
    id: 'masters',
    label: 'Masters',
    items: [
      { moduleId: 'items',         label: 'Item Master',     path: '/masters/items' },
      { moduleId: 'parties',       label: 'Party Master',    path: '/masters/parties' },
      { moduleId: 'price-master',  label: 'Price Master',    path: '/masters/price-master' },
      { moduleId: 'locations',     label: 'Location Master', path: '/masters/locations' },
      { moduleId: 'sis-commission', label: 'Commission & Settlement', path: '/masters/commission' },
      { moduleId: 'hsn-gst-master', label: 'HSN GST Master', path: '/masters/hsn-gst' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { moduleId: 'settings',       label: 'Settings',          path: '/admin/settings' },
      { moduleId: 'configurations', label: 'Configurations',    path: '/admin/configurations' },
      { moduleId: 'usermgmt',       label: 'User Management',   path: '/admin/usermgmt' },
      { moduleId: 'rolemgmt',       label: 'Role Management',   path: '/admin/rolemgmt' },
      { moduleId: 'audit-logs',     label: 'Audit Logs',        path: '/admin/audit-logs' },
    ],
  },
];

// DASHBOARD now lives inside the 'dashboards' group, so it's covered by the flat-map.
export const ALL_ITEMS: SidebarItem[] = SIDEBAR_GROUPS.flatMap((g) => g.items);
